import { useTranslation } from 'react-i18next';
import type { Dispatch, SetStateAction } from 'react';
import * as apiProduct from 'api/apiProducts';
import { useToast } from 'context/ToastContext';
import type { ProductEditType } from '../../types';

type SaveProductCardDeps = {
    fetchProducts: (force?: boolean) => Promise<unknown>;
    setProduct: Dispatch<SetStateAction<ProductEditType | null>>;
    setShowEditProduct: Dispatch<SetStateAction<number | false>>;
};

export type SaveProductCardOptions = {
    entityId?: number;
    /** Закрити картку після успішного збереження (кнопка «Зберегти») */
    close?: boolean;
};

export type SaveProductCardResult =
    | { ok: true; entityId: number; values: ProductEditType }
    | { ok: false };

/**
 * Спільне збереження картки товару (валідація + API) для вкладки «Загальні» і «Файли».
 */
const useSaveProductCard = ({ fetchProducts, setProduct, setShowEditProduct }: SaveProductCardDeps) => {
    const { t } = useTranslation(['Products', 'common']);
    const { showToast } = useToast();

    const saveProductCard = async (
        values: ProductEditType,
        options: SaveProductCardOptions = {},
    ): Promise<SaveProductCardResult> => {
        const article = String(values.prodArticle || '').trim();
        const name = String(values.prodName || '').trim();
        const vendorId = Number(values.vendorId || 0);
        const dimensionId = Number(values.dimensionId || 0);
        const categoryId = Number(values.categoryId || 0);

        if (!article) {
            showToast(t('messEmptyArticle'), 'danger');
            return { ok: false };
        }
        if (!name) {
            showToast(t('messEmptyName'), 'danger');
            return { ok: false };
        }
        if (!Number.isFinite(vendorId) || vendorId < 0) {
            showToast(t('messEmptyVendor'), 'danger');
            return { ok: false };
        }
        if (dimensionId <= 0) {
            showToast(t('messEmptyDimension'), 'danger');
            return { ok: false };
        }

        const entityId = Number(options.entityId ?? values.ID ?? 0);
        const payload: ProductEditType = {
            ...values,
            prodArticle: article,
            prodName: name,
            vendorId,
            dimensionId,
            categoryId,
        };

        const resSave = await apiProduct.save(entityId, payload);
        if (!resSave) {
            showToast(t('common:messError'), 'danger');
            return { ok: false };
        }
        if (resSave.articleDublicate) {
            showToast(t('messArticleExists'), 'danger');
            return { ok: false };
        }
        if (!resSave.stat) {
            showToast(t('common:messError'), 'danger');
            return { ok: false };
        }

        const savedId = entityId > 0 ? entityId : Number(resSave.stat);
        if (!Number.isFinite(savedId) || savedId <= 0) {
            showToast(t('common:messError'), 'danger');
            return { ok: false };
        }

        const nextValues: ProductEditType = { ...payload, ID: savedId };
        setProduct((prev) => (prev ? { ...prev, ...nextValues } : nextValues));
        await fetchProducts(true);

        if (options.close) {
            setShowEditProduct(false);
            showToast(t('messProductSaved'), 'success');
            setTimeout(() => {
                setProduct(null);
            }, 300);
        }

        return { ok: true, entityId: savedId, values: nextValues };
    };

    return { saveProductCard };
};

export default useSaveProductCard;
